import React, { useState, useEffect, useRef } from "react";
import FormModal from "./FormModal";
import PropTypes from "prop-types";
import { Toast } from "primereact/toast";
import interactiveRegistry from "~/Registry/Interactive/InteractiveRegistry";
import { useChartsStore } from "~/store/chartStore";
import { useIndicatorDefaultsStore } from "~/store/indicatorDefaultsStore";
import {
  interactiveDefaultsKey,
  mergeCustomAppearance,
} from "../ChartWrapper/utils/interactiveAppearance";

// Using copilot-instructions to structure edit modal for interactive objects
// chartId: the actual chart store ID
// interactive: the interactive object from context menu (with type, object, moreProps)
// visible: boolean
// onClose: () => void
// onSave: (interactiveId, appearanceOptions) => void
const InteractiveEditModal = ({
  chartId,
  interactive,
  visible,
  onClose,
  onSave,
  hostBody = null,
}) => {
  // Track if close was intentionally triggered by our buttons
  const intentionalCloseRef = useRef(false);

  const toast = useRef(null);

  // Issue #339: defaults customizados por tipo de objeto ("Usar como Padrão"),
  // persistidos no mesmo blob server-synced dos indicadores, chave namespaced.
  const getAppearanceDefaults = useIndicatorDefaultsStore(
    (s) => s.getAppearanceDefaults,
  );
  const saveAppearanceDefaults = useIndicatorDefaultsStore(
    (s) => s.saveAppearanceDefaults,
  );
  const resetAppearanceDefaults = useIndicatorDefaultsStore(
    (s) => s.resetAppearanceDefaults,
  );

  // Counter to force FormModal remount on each open (fixes dropdown z-index caching issue)
  const openCounterRef = useRef(0);
  const [modalKey, setModalKey] = useState(0);

  // Increment counter when modal becomes visible to force dropdown panel recreation
  useEffect(() => {
    if (visible) {
      openCounterRef.current += 1;
      setModalKey(openCounterRef.current);
    }
  }, [visible]);

  // Access the chart store to get current interactive object appearance
  const charts = useChartsStore((state) => state.charts);
  // Get registry entry for this interactive type
  const getRegistryEntry = () => {
    if (!interactive?.type) return null;

    // Search through all categories to find the interactive entry
    for (const categoryKey in interactiveRegistry) {
      const category = interactiveRegistry[categoryKey];
      if (category.interactives && category.interactives[interactive.type]) {
        return category.interactives[interactive.type];
      }
    }
    return null;
  };

  const registryEntry = getRegistryEntry();

  // Get current appearance from chartStore
  const getCurrentAppearanceFromStore = () => {
    if (!interactive?.type || !interactive?.chartId || !chartId) {
      console.log("📝 Modal: Missing required data for appearance lookup:", {
        interactive,
        chartId,
      });
      return {};
    }

    // Convert string IDs to numbers for consistent comparison
    const numericChartId =
      typeof chartId === "string"
        ? chartId.match(/^[0-9]+$/)
          ? parseInt(chartId, 10)
          : chartId
        : chartId;
    const numericChartSectionId =
      typeof interactive.chartId === "string"
        ? interactive.chartId.match(/^[0-9]+$/)
          ? parseInt(interactive.chartId, 10)
          : interactive.chartId
        : interactive.chartId;

    // Key format matches how InteractiveLayer stores objects
    const key = numericChartSectionId
      ? `${numericChartId}_${numericChartSectionId}`
      : numericChartId;

    const chart = charts.find((c) => c.id === numericChartId);
    const objects = chart?.interactiveObjects?.[key]?.[interactive.type];
    const objectIndex = interactive.index || 0;
    const currentObject = objects?.[objectIndex];

    console.log("📝 Modal appearance lookup:", {
      numericChartId,
      numericChartSectionId,
      key,
      chartFound: !!chart,
      interactiveType: interactive.type,
      objectIndex,
      objectsCount: objects?.length || 0,
      currentObject: currentObject ? "found" : "not found",
      appearance: currentObject?.appearance || "none",
      flatFont: currentObject?.font || "none",
      flatStroke: currentObject?.stroke || "none",
    });

    // All interactive types now use nested appearance
    const currentAppearance = currentObject?.appearance || {};
    console.log(
      "📝 Modal using nested appearance for",
      interactive.type,
      ":",
      currentAppearance,
    );
    return currentAppearance;
  };

  // Ensure appearanceConfig is { [name]: defaultValue }
  const getInitialAppearanceOptions = () => {
    if (!registryEntry?.defaultOptionsForAppearance) return {};

    // Get current appearance from chartStore
    const currentAppearance = getCurrentAppearanceFromStore();

    // If we have stored appearance, use it directly - no need to merge with defaults
    if (currentAppearance && Object.keys(currentAppearance).length > 0) {
      return currentAppearance;
    }

    // Fallback: create from registry defaults (for new objects),
    // merged with the user's saved defaults for this type (issue #339)
    const defaultOptions = registryEntry.defaultOptionsForAppearance.reduce(
      (acc, prop) => {
        acc[prop.name] = prop.defaultValue;
        return acc;
      },
      {},
    );

    return mergeCustomAppearance(
      defaultOptions,
      getAppearanceDefaults(interactiveDefaultsKey(interactive?.type)),
    );
  };

  const [appearanceConfig, setAppearanceConfig] = useState(
    getInitialAppearanceOptions,
  );

  useEffect(() => {
    if (visible && interactive) {
      console.log("📝 Modal useEffect - setting appearance config");
      const initialOptions = getInitialAppearanceOptions();
      console.log("📝 Modal useEffect - initialOptions:", initialOptions);
      setAppearanceConfig(initialOptions);
    }
  }, [visible, interactive]);

  const handleAppearanceChange = (propName, field, fieldValue) => {
    setAppearanceConfig((prev) => {
      // Support direct property replacement (for array values like FibLevelsType)
      if (field === null || field === undefined) {
        return { ...prev, [propName]: fieldValue };
      }
      const newConfig = {
        ...prev,
        [propName]: {
          ...(prev[propName] || {}),
          [field]: fieldValue,
        },
      };
      return newConfig;
    });
  };

  const handleSave = () => {
    // Generate a unique ID for this interactive object based on its properties and context

    // Use the correct IDs:
    // - chartId (from props) = main chart store ID
    // - interactive.chartId = chart section ID (1 for main, other numbers for indicator charts)
    const actualChartId = chartId; // Main chart store ID
    const chartSectionId = interactive.chartId; // Chart section ID from the interactive object
    const objectType = interactive.type;
    const objectIndex = interactive.index || 0;
    const interactiveId = `${actualChartId}_${chartSectionId}_${objectType}_${objectIndex}`;

    // Simply save the complete appearance config as-is
    // The appearanceConfig already contains the complete appearance object

    intentionalCloseRef.current = true;
    onSave(interactiveId, appearanceConfig);
    onClose();
  };

  const handleCancel = () => {
    intentionalCloseRef.current = true;
    onClose();
  };

  // Pure registry defaults (ignoring saved defaults and the current object)
  const getRegistryAppearanceOptions = () =>
    (registryEntry?.defaultOptionsForAppearance || []).reduce((acc, prop) => {
      acc[prop.name] = prop.defaultValue;
      return acc;
    }, {});

  // Issue #339: save current appearance as the default for this object type —
  // new drawings of this type are born with it (overrides the global swatch)
  const handleSaveDefaults = () => {
    if (!interactive?.type) return;
    saveAppearanceDefaults(
      interactiveDefaultsKey(interactive.type),
      appearanceConfig,
    );
    toast.current?.show({
      severity: "success",
      summary: "Padrão Salvo",
      detail: `Aparência padrão salva para ${registryEntry?.displayName}`,
      life: 3000,
    });
  };

  // Issue #339: remove the saved default and reload fields from the registry
  const handleResetDefaults = () => {
    if (!interactive?.type) return;
    resetAppearanceDefaults(interactiveDefaultsKey(interactive.type));
    setAppearanceConfig(getRegistryAppearanceOptions());
    toast.current?.show({
      severity: "info",
      summary: "Padrão Resetado",
      detail: `Aparência padrão resetada para ${registryEntry?.displayName}`,
      life: 3000,
    });
  };

  if (!registryEntry) return null;

  // Only allow close through explicit button actions (Save/Cancel/X)
  const handleBeforeHide = (event) => {
    if (intentionalCloseRef.current) {
      intentionalCloseRef.current = false;
      return true;
    }
    // Allow close via X button in dialog header
    const target = event?.target || event?.originalEvent?.target;
    if (target) {
      const isCloseIcon =
        target.closest(".p-dialog-header-close") ||
        target.classList?.contains("pi-times");
      if (isCloseIcon) return true;
    }
    // Block all other close attempts (mask clicks, focus changes, input interactions, etc.)
    return false;
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <FormModal
        key={`modal_${modalKey}`}
        title={`Editar aparência: ${registryEntry.displayName}`}
        visible={visible}
        onHide={handleCancel}
        hostBody={hostBody}
        onCancel={handleCancel}
        onSave={handleSave}
        size="large"
        onBeforeHide={handleBeforeHide}
        extraActions={
          <div style={{ display: "flex", gap: "3px" }}>
            <button
              type="button"
              className="p-button p-button-sm p-button-outlined"
              onClick={handleSaveDefaults}
              title="Salvar a aparência atual como padrão para novos objetos deste tipo"
              style={{ fontSize: "11px", padding: "4px 8px" }}
              data-evo-id="interactiveeditmodal-btn-save-defaults"
            >
              <i
                className="pi pi-save"
                style={{ marginRight: "3px", fontSize: "11px" }}
              ></i>
              Usar como Padrão
            </button>
            <button
              type="button"
              className="p-button p-button-sm p-button-outlined p-button-secondary"
              onClick={handleResetDefaults}
              title="Resetar o padrão customizado deste tipo de objeto"
              style={{ fontSize: "11px", padding: "4px 8px" }}
              data-evo-id="interactiveeditmodal-btn-reset-defaults"
            >
              <i
                className="pi pi-refresh"
                style={{ marginRight: "3px", fontSize: "11px" }}
              ></i>
              Original
            </button>
          </div>
        }
      >
        <div
          className="indicator-modal-content"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {/* Appearance options */}
          {registryEntry.defaultOptionsForAppearance &&
            registryEntry.defaultOptionsForAppearance.length > 0 && (
              <div className="options-form">
                {registryEntry.defaultOptionsForAppearance.map((prop, idx) => {
                  try {
                    // Read stored value and fallback to registry default
                    const rawValue = appearanceConfig[prop.name];
                    const defaultValue = prop.defaultValue;

                    // Normalize value to avoid uncontrolled -> controlled warnings.
                    // If defaultValue is an object, merge defaults so nested fields exist.
                    let value;
                    if (rawValue === undefined || rawValue === null) {
                      // If defaultValue is defined, use it; otherwise prefer empty string/object
                      if (defaultValue !== undefined) {
                        value = defaultValue;
                      } else {
                        value = typeof defaultValue === "object" ? {} : "";
                      }
                    } else if (
                      defaultValue &&
                      typeof defaultValue === "object" &&
                      typeof rawValue === "object" &&
                      !Array.isArray(defaultValue) &&
                      !Array.isArray(rawValue)
                    ) {
                      value = { ...defaultValue, ...rawValue };
                    } else {
                      value = rawValue;
                    }

                    return (
                      <div
                        key={prop.name || idx}
                        className="indicator-modal-field"
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          marginBottom: "1rem",
                        }}
                      >
                        <label
                          htmlFor={prop.name}
                          className="indicator-modal-label"
                          style={{
                            minWidth: "8.5rem",
                            marginRight: "1rem",
                            textAlign: "right",
                          }}
                        >
                          {prop.displayName}
                        </label>
                        <div style={{ flex: 1 }}>
                          {prop.render(value, (field, fieldValue) =>
                            handleAppearanceChange(
                              prop.name,
                              field,
                              fieldValue,
                            ),
                          )}
                        </div>
                      </div>
                    );
                  } catch (error) {
                    return (
                      <div
                        key={prop.name || idx}
                        className="indicator-modal-error"
                      >
                        <strong>Erro ao renderizar aparência:</strong>{" "}
                        {prop.name}
                        <br />
                        <span>{error.message}</span>
                      </div>
                    );
                  }
                })}
              </div>
            )}

          {/* Show message if no appearance options */}
          {(!registryEntry.defaultOptionsForAppearance ||
            registryEntry.defaultOptionsForAppearance.length === 0) && (
            <div
              style={{ textAlign: "center", padding: "2rem", color: "#666" }}
            >
              <p>Este objeto não possui opções de aparência configuráveis.</p>
            </div>
          )}
        </div>
      </FormModal>
    </>
  );
};

InteractiveEditModal.propTypes = {
  chartId: PropTypes.string.isRequired,
  hostBody: PropTypes.object,
  interactive: PropTypes.shape({
    type: PropTypes.string.isRequired,
    chartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    object: PropTypes.shape({
      appearance: PropTypes.object,
    }),
    moreProps: PropTypes.shape({
      chartConfig: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
    index: PropTypes.number,
  }).isRequired,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default InteractiveEditModal;
